import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { userTaskWorkflowService } from "../../services/userTask/UserTaskWorkflowService";
import { userTaskService } from "../../services/userTask/UserTaskService";
import { CreateWorkflowTaskRequest, ManualStepUpdateRequest, VerifyTasksRequest } from "../../interfaces/DTO/userTask/UserTaskWorkflow";
import { UserTaskType } from "../../interfaces/DTO/userTask/UserTask";

const KEYS = {
  allWithWorkflow: ["tasks_workflow"] as const,
  details: (taskId: number) => ["task_workflow_details", taskId] as const,
  workflowDefinition: (taskType: UserTaskType, changeType?: string) =>
    ["workflow_definition", taskType, changeType ?? null] as const,
};

// Query key used by useTasksWithWorkflow, exposed so other hooks (e.g. task deletion)
// can invalidate this cache too - it is separate from the plain "user_tasks" cache.
export const WORKFLOW_TASKS_QUERY_KEY = KEYS.allWithWorkflow;

// changeType disambiguates task_types with more than one WorkflowDefinition (e.g.
// DEPOSIT_TRACKING covers both a one-time deposit and a recurring-deposit change) -
// see backend WorkflowRegistry.get(). Omit it for task_types with a single/default workflow.
export function useWorkflowDefinition(taskType: UserTaskType | null, changeType?: string) {
  return useQuery({
    queryKey: KEYS.workflowDefinition(taskType!, changeType),
    queryFn: () => userTaskWorkflowService.fetchWorkflowDefinition(taskType!, changeType),
    enabled: taskType != null,
    staleTime: 5 * 60_000,
  });
}

export function useTasksWithWorkflow() {
  return useQuery({
    queryKey: KEYS.allWithWorkflow,
    queryFn: () => userTaskWorkflowService.fetchAllWithWorkflow(),
    staleTime: 10_000,
  });
}

export const FORMS_PREPARATION_QUERY_KEY = ["forms_preparation"] as const;

/** the clients' tasks whose forms are not prepared yet (עזרה בהכנת טפסים) */
export function useFormsPreparation(contactIds: number[]) {
  return useQuery({
    queryKey: [...FORMS_PREPARATION_QUERY_KEY, contactIds.join(",")],
    queryFn: () => userTaskWorkflowService.fetchFormsPreparation(contactIds),
    enabled: contactIds.length > 0,
    staleTime: 10_000,
  });
}

/** completes a step of any task (the forms helper moves between tasks) and refreshes the task lists */
export function useCompleteTaskStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, stepCode }: { taskId: number; stepCode: string }) =>
      userTaskWorkflowService.completeStep(taskId, stepCode),
    onSuccess: (_details, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: KEYS.details(taskId) });
      queryClient.invalidateQueries({ queryKey: KEYS.allWithWorkflow });
      queryClient.invalidateQueries({ queryKey: FORMS_PREPARATION_QUERY_KEY });
    },
  });
}

export function useTaskWorkflowDetails(taskId: number | null) {
  return useQuery({
    queryKey: KEYS.details(taskId!),
    queryFn: () => userTaskWorkflowService.fetchTaskDetails(taskId!),
    enabled: taskId != null,
    staleTime: 5_000,
  });
}

export function useCompleteWorkflowStep(taskId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      stepCode,
      resultData,
    }: {
      stepCode: string;
      resultData?: Record<string, unknown>;
    }) => userTaskWorkflowService.completeStep(taskId, stepCode, resultData),
    onSuccess: (updatedDetails) => {
      queryClient.setQueryData(KEYS.details(taskId), updatedDetails);
      queryClient.invalidateQueries({ queryKey: KEYS.allWithWorkflow });
    },
  });
}

export function useManualUpdateStep(taskId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepCode, body }: { stepCode: string; body: ManualStepUpdateRequest }) =>
      userTaskWorkflowService.manualUpdateStep(taskId, stepCode, body),
    onSuccess: (updatedDetails) => {
      queryClient.setQueryData(KEYS.details(taskId), updatedDetails);
      queryClient.invalidateQueries({ queryKey: KEYS.allWithWorkflow });
    },
  });
}

export function useCreateWorkflowTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkflowTaskRequest) =>
      userTaskWorkflowService.createWithWorkflow(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.allWithWorkflow });
    },
  });
}

// Verifies tasks for ALL contacts in the company. Kept as a distinct hook (rather than
// one hook with an optional contact_id) so a page can never accidentally call the
// company-wide verification when it meant to scope to a single contact, or vice versa.
export function useVerifyAllTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: VerifyTasksRequest) => userTaskWorkflowService.verifyAllTasks(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.allWithWorkflow });
    },
  });
}

// Verifies tasks for one contact only. contactId is a required argument (not part of an
// optional request body) so a caller cannot forget to pass it.
export function useVerifyContactTasks(contactId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: VerifyTasksRequest) =>
      userTaskWorkflowService.verifyContactTasks(contactId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.allWithWorkflow });
    },
  });
}

// Deletes a task and invalidates both the plain "user_tasks" cache (via useDeleteUserTask's
// own cache patching) and the "tasks_workflow" cache used by tables built on useTasksWithWorkflow -
// deleting only through useDeleteUserTask leaves the latter stale.
export function useDeleteWorkflowTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => userTaskService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.allWithWorkflow });
    },
  });
}
